import type { ReactNode } from "react";
import type { StockItem } from "../../stock/model";
import type { StockAnswer } from "../../stock/sheet";
import Icon from "../components/Icon";
import { CountChart, WeeklyBars } from "./charts";
import RequestEditor, { type SaveRequest } from "./RequestEditor";
import { agoText, daysAgo, fmt, shopColor, shortDate, STATUS, weekNumber } from "./look";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function Figure({ label, value, note }: { label: string; value: ReactNode; note?: ReactNode }) {
  return (
    <div className="rounded-2xl bg-blush-soft px-3 py-2.5">
      <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-faint">{label}</div>
      <div className="mt-0.5 text-[20px] font-bold tabular-nums leading-tight">{value}</div>
      {note && <div className="mt-0.5 text-[12px] leading-snug text-ink-soft">{note}</div>}
    </div>
  );
}

function Section({ title, children, aside }: { title: string; children: ReactNode; aside?: ReactNode }) {
  return (
    <section className="border-t border-line-soft pt-4">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <h3 className="text-[14px] font-bold">{title}</h3>
        {aside && <span className="text-[12px] text-ink-faint">{aside}</span>}
      </div>
      {children}
    </section>
  );
}

const days = (n: number | null) => (n === null ? "–" : n % 30 === 0 && n >= 30 ? `${n / 30} mo` : n % 7 === 0 && n >= 7 ? `${n / 7} wk` : `${n} d`);

/** Everything about one item: where it stands, how long it lasts, who asks for it, and what the sheet says. */
// `shop` and `week` are the page's: the requested amount can be changed there, above the status.
export default function ItemPanel({ item, data, onClose, shop, week, save }: { item: StockItem; data: StockAnswer; onClose: () => void; shop: string | null; week: string; save: SaveRequest }) {
  const status = STATUS[item.status];
  const shops = data.shops;
  const recent = data.weeks.slice(-16);
  const value = (shop: string, week: string) => item.weekly[shop]?.[week] ?? 0;
  const askingShops = shops.filter((s) => item.weekly[s] || item.notes[s]);
  const year = data.today.slice(0, 4);
  const lastFour = data.weeks.slice(-4);
  const rateWeeks = data.weeks.filter((w) => w < data.currentWeek).slice(-12);
  const unit = item.packedBy === "kitchen" && !item.warehouse ? "" : "boxes";
  const supplierLead = item.orderDays !== null || item.productionDays !== null;
  const sheetLink = `https://docs.google.com/spreadsheets/d/${data.source.sheetId}/edit`;

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-[12px] font-semibold text-ink-faint">
            {item.category} · {item.packedBy === "kitchen" ? "packed by the kitchen" : "packed in the warehouse"}
          </div>
          <h2 className="text-[22px] font-bold leading-tight tracking-[-0.02em]">{item.name}</h2>
          {item.supplier && <div className="text-[13px] text-ink-soft">From {item.supplier}{item.perBox ? ` · ${fmt(item.perBox)} per box` : ""}{item.perPallet ? ` · ${fmt(item.perPallet)} per pallet` : ""}</div>}
        </div>
        <button onClick={onClose} aria-label="Close" className="flex size-10 shrink-0 items-center justify-center rounded-full bg-blush text-rose-ink active:bg-[#f8d7dd]">
          <Icon name="close" size={20} />
        </button>
      </header>

      <RequestEditor item={item} data={data} shop={shop} week={week} save={save} />

      <div className={`flex items-start gap-2.5 rounded-2xl px-3.5 py-3 ${status.pill}`}>
        <span className="mt-px flex size-5 shrink-0 items-center justify-center rounded-full bg-white/70 text-[12px] font-bold">{status.mark}</span>
        <div className="text-[14px] leading-snug">
          <span className="font-bold">{status.label}.</span> {item.reason}
        </div>
      </div>

      {item.warehouse || item.kitchen ? (
        <div className="grid grid-cols-2 gap-2">
          {item.warehouse && (
            <Figure
              label="Counted"
              value={fmt(item.warehouse.last)}
              note={`${shortDate(item.warehouse.at)}, ${agoText(daysAgo(item.warehouse.at!, data.today))}`}
            />
          )}
          {item.warehouse && (
            <Figure label="Today, about" value={fmt(item.nowEstimate)} note={item.sentSinceCount ? `${fmt(item.sentSinceCount)} requested since the count` : "Nothing requested since the count"} />
          )}
          {item.kitchen && <Figure label="In the kitchen" value={fmt(item.kitchen.last)} note={`Counted ${shortDate(item.kitchen.at)}`} />}
          {item.supplierBoxes !== null && (
            <Figure label="At the supplier" value={fmt(item.supplierBoxes)} note={item.supplierStock !== null && Math.round(item.supplierStock) !== Math.round(item.supplierBoxes) ? `boxes (${fmt(item.supplierStock)} items)` : "boxes"} />
          )}
          <Figure label="Per week" value={fmt(item.perWeek)} note={item.perMonth !== null ? `requested, last 12 weeks · the sheet says ${fmt(item.sheet.monthlyAverage ?? item.perMonth)} a month` : "requested, last 12 weeks"} />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <Figure label="Per week" value={fmt(item.perWeek)} note="requested, last 12 weeks" />
          <Figure label="Counted" value="–" note="Not on the warehouse or kitchen count" />
        </div>
      )}

      {item.orderBy && (
        <Section title="When to order" aside={supplierLead ? `${item.supplier ?? "Supplier"}: ${days(item.orderDays)} to deliver${item.productionDays ? `, ${days(item.productionDays)} to make` : ""}` : undefined}>
          <div className="grid grid-cols-3 gap-2 text-[13px]">
            <div>
              <div className="text-ink-faint">Lasts</div>
              <div className="font-bold">{fmt(item.weeksLeft)} weeks</div>
              {item.weeksHere !== null && item.weeksHere !== item.weeksLeft && <div className="text-ink-soft">{fmt(item.weeksHere)} in the warehouse</div>}
            </div>
            <div>
              <div className="text-ink-faint">{item.orderKind === "call-off" ? "Call off by" : item.orderKind === "batch" ? "New batch by" : "Order by"}</div>
              <div className="font-bold">{item.orderBy <= data.today ? "Now" : shortDate(item.orderBy)}</div>
              <div className="text-ink-soft">runs out {shortDate(item.runsOut)}</div>
            </div>
            <div>
              <div className="text-ink-faint">{item.orderKind === "call-off" ? "Bring in" : "Order"}</div>
              <div className="font-bold">≈ {fmt(item.suggest)} boxes</div>
              {item.perBox && item.suggest && <div className="text-ink-soft">{fmt(item.suggest * item.perBox)} items</div>}
            </div>
          </div>
          <p className="mt-2 text-[12px] leading-snug text-ink-faint">
            Worked out from the pace of the last 12 weeks, the lead time and a week to spare. {item.orderKind === "call-off" ? "The supplier keeps stock for us, so the first step is bringing some in." : ""}
          </p>
        </Section>
      )}

      {item.warehouse && item.warehouse.history.length > 1 && (
        <Section title="Warehouse counts" aside={item.arrivals.length ? `${item.arrivals.length} deliveries seen` : undefined}>
          <CountChart counted={item.warehouse} nowEstimate={item.nowEstimate} today={data.today} red={item.sheet.red} unit={unit} />
        </Section>
      )}

      {askingShops.length > 0 && (
        <Section title="Requested by the shops" aside="last 16 weeks">
          <WeeklyBars weeks={recent} shops={askingShops} allShops={shops} value={value} note={(s, w) => item.notes[s]?.[w] ?? []} />
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-[12px] tabular-nums">
              <thead>
                <tr className="text-left text-ink-faint">
                  <th className="py-1 pr-2 font-semibold">Shop</th>
                  {lastFour.map((w) => (
                    <th key={w} className="px-1 py-1 text-right font-semibold">w{weekNumber(w)}</th>
                  ))}
                  <th className="px-1 py-1 text-right font-semibold">Per week</th>
                  <th className="py-1 pl-1 text-right font-semibold">{year}</th>
                </tr>
              </thead>
              <tbody>
                {askingShops.map((s) => {
                  const perWeek = rateWeeks.reduce((t, w) => t + value(s, w), 0) / Math.max(1, rateWeeks.length);
                  const total = Object.entries(item.weekly[s] ?? {}).reduce((t, [w, n]) => t + (w.startsWith(year) ? n : 0), 0);
                  return (
                    <tr key={s} className="border-t border-line-soft">
                      <td className="py-1.5 pr-2">
                        <span className="flex items-center gap-1.5">
                          <span className="size-2 shrink-0 rounded-[2px]" style={{ background: shopColor(shops, s) }} />
                          {s}
                        </span>
                      </td>
                      {lastFour.map((w) => (
                        <td key={w} className="px-1 py-1.5 text-right">{[value(s, w) ? fmt(value(s, w)) : "", ...(item.notes[s]?.[w] ?? [])].filter(Boolean).join(" + ") || <span className="text-ink-faint">·</span>}</td>
                      ))}
                      <td className="px-1 py-1.5 text-right">{fmt(perWeek)}</td>
                      <td className="py-1.5 pl-1 text-right font-semibold">{fmt(total)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {item.supplierLog && (
        <Section title={`${item.supplier ?? "Supplier"} in ${item.supplierLog.year}`} aside="items, from the sheet's log">
          <div className="overflow-x-auto">
            <table className="w-full text-[11px] tabular-nums">
              <thead>
                <tr className="text-ink-faint">
                  <th />
                  {MONTHS.map((mo) => (
                    <th key={mo} className="px-1 text-right font-semibold">{mo}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(["ordered", "used"] as const).map((row) => (
                  <tr key={row} className="border-t border-line-soft">
                    <td className="py-1 pr-2 font-semibold">{row === "ordered" ? "Ordered" : "Used"}</td>
                    {item.supplierLog![row].map((n, i) => (
                      <td key={i} className="px-1 py-1 text-right">{n ? fmt(n / 1000) + "k" : <span className="text-ink-faint">·</span>}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      <Section title="In the sheet">
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-[13px]">
          <dt className="text-ink-faint">Monthly average</dt>
          <dd className="text-right tabular-nums">{fmt(item.sheet.monthlyAverage)}</dd>
          <dt className="text-ink-faint">Ideal level</dt>
          <dd className="text-right tabular-nums">{fmt(item.sheet.ideal)} {item.sheet.ideal !== null && "boxes"}</dd>
          <dt className="text-ink-faint">Red level</dt>
          <dd className="text-right tabular-nums">{fmt(item.sheet.red)} {item.sheet.red !== null && "boxes"}</dd>
          {(item.sheet.supplierIdeal !== null || item.sheet.supplierRed !== null) && (
            <>
              <dt className="text-ink-faint">At the supplier: ideal / red</dt>
              <dd className="text-right tabular-nums">{fmt(item.sheet.supplierIdeal)} / {fmt(item.sheet.supplierRed)}</dd>
            </>
          )}
          {item.ordered && (
            <>
              <dt className="text-ink-faint">Ordered</dt>
              <dd className="text-right tabular-nums">{item.ordered}</dd>
            </>
          )}
        </dl>
        {item.issues.length > 0 && (
          <ul className="mt-3 flex flex-col gap-1.5">
            {item.issues.map((issue) => (
              <li key={issue} className="flex gap-2 rounded-xl bg-[#fdf0d2] px-3 py-2 text-[12px] leading-snug text-[#5c3d00]">
                <span aria-hidden="true">◷</span>
                {issue}
              </li>
            ))}
          </ul>
        )}
        <a href={sheetLink} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-semibold text-rose-deep">
          Open the sheet <Icon name="external" size={14} />
        </a>
      </Section>
    </div>
  );
}
